import type {
  ICandidate,
  FormMessage,
  FormMessageMedium,
  FormMessageTemplateKey,
  FormFileRequest,
  FileRequestMacroKey,
  FormUserSignature,
} from '@/types/candidate';
import { request } from './client';
import { sortOldestFirst } from '@/helpers/candidateMessaging';
import type { RequestOptions } from './client';
import type { CandidateBoard, Swimlane } from '@/types/kanban';

/**
 * Fetch the candidate kanban board (swimlanes) for an order.
 */
export async function fetchCandidateBoard(orderId: number | string, search?: string): Promise<CandidateBoard> {
  const params: Record<string, string | number> = { order_id: orderId };
  if (search) params.search = search;

  const response = await request('GET', '/v1/account/candidates/board', params);
  return response.data as CandidateBoard;
}

/**
 * Move one or more candidates to a target swimlane within an order.
 * A single move is just a one-element array.
 */
export async function moveCandidates(
  orderId: number | string,
  candidateEntityIds: string[],
  statusLaneKey: string,
  requestOptions: RequestOptions = {}
): Promise<{ moved: number; status_lane_key: string }> {
  const response = await request(
    'POST',
    '/v1/account/candidates/move',
    {},
    { order_id: Number(orderId), candidate_entity_ids: candidateEntityIds, status_lane_key: statusLaneKey },
    requestOptions
  );
  return response.data as { moved: number; status_lane_key: string };
}

/* --------------------------------- SWIMLANES ------------------------------ */
// all swimlane mutations require LEAD admin rights and return the full, updated swimlane list

export async function fetchSwimlanes(): Promise<Swimlane[]> {
  const response = await request('GET', '/v1/account/candidates/swimlanes');
  return response.data as Swimlane[];
}

export async function createSwimlane(name: string): Promise<Swimlane[]> {
  const response = await request('POST', '/v1/account/candidates/swimlanes', {}, { name });
  return response.data as Swimlane[];
}

export async function renameSwimlane(laneKey: string, name: string): Promise<Swimlane[]> {
  const response = await request(
    'PUT',
    `/v1/account/candidates/swimlanes/${encodeURIComponent(laneKey)}`,
    {},
    { name }
  );
  return response.data as Swimlane[];
}

export async function deleteSwimlane(laneKey: string, requestOptions: RequestOptions = {}): Promise<Swimlane[]> {
  const response = await request(
    'DELETE',
    `/v1/account/candidates/swimlanes/${encodeURIComponent(laneKey)}`,
    {},
    {},
    requestOptions
  );
  return response.data as Swimlane[];
}

export async function reorderSwimlanes(orderedKeys: string[]): Promise<Swimlane[]> {
  const response = await request('POST', '/v1/account/candidates/swimlanes/reorder', {}, { ordered_keys: orderedKeys });
  return response.data as Swimlane[];
}

/* --------------------------------- CANDIDATE ------------------------------ */

export async function getCandidate(candidateEntityId: string): Promise<ICandidate> {
  const response = await request('GET', `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}`);
  return response.data as ICandidate;
}

// downloads a single file (e.g. a CV) previously submitted by the candidate, identified by the
// file_sha256 returned inline on candidate.applications[].files[]
export async function downloadCandidateFile(candidateEntityId: string, fileSha256: string): Promise<Blob> {
  const blob = await request('GET', `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/files`, {
    file_sha256: fileSha256,
  });
  return blob as Blob;
}

export async function closeCandidate(
  candidateEntityId: string,
  orderId: number | string,
  requestOptions: RequestOptions = {}
): Promise<void> {
  await request(
    'POST',
    '/v1/account/candidates/close',
    {},
    { candidate_entity_id: candidateEntityId, order_id: Number(orderId) },
    requestOptions
  );
}

export async function discardCandidate(
  candidateEntityId: string,
  orderId: number | string,
  requestOptions: RequestOptions = {}
): Promise<void> {
  await request(
    'POST',
    '/v1/account/candidates/discard',
    {},
    { candidate_entity_id: candidateEntityId, order_id: Number(orderId) },
    requestOptions
  );
}

export async function hireCandidate(
  candidateEntityId: string,
  orderId: number | string,
  requestOptions: RequestOptions = {}
): Promise<void> {
  await request(
    'POST',
    '/v1/account/candidates/hire',
    {},
    { candidate_entity_id: candidateEntityId, order_id: Number(orderId) },
    requestOptions
  );
}

// permanently removes the candidate and all of their personal data/applications; unlike discardCandidate this cannot be undone
export async function fullDeleteCandidate(
  candidateEntityId: string,
  requestOptions: RequestOptions = {}
): Promise<void> {
  await request('DELETE', `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}`, {}, {}, requestOptions);
}

/* --------------------------------- MESSAGES -------------------------------- */

// an application belongs to one order, so its thread only lists that order's messages
export async function fetchCandidateMessages(
  candidateEntityId: string,
  orderId?: number | null
): Promise<FormMessage[]> {
  const response = await request(
    'GET',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/messages`,
    orderId ? { order_id: orderId } : {}
  );
  return sortOldestFirst(response.data as FormMessage[]);
}

export async function sendCandidateMessage(
  candidateEntityId: string,
  payload: {
    medium: FormMessageMedium;
    body: string;
    subject?: string;
    template_key?: FormMessageTemplateKey;
    publication_id?: number;
    order_id?: number;
  }
): Promise<FormMessage> {
  const response = await request(
    'POST',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/messages`,
    {},
    payload
  );
  return response.data as FormMessage;
}

export async function previewMessageTemplate(
  candidateEntityId: string,
  templateKey: FormMessageTemplateKey,
  orderId: number
): Promise<{ subject: string; body: string }> {
  const response = await request(
    'GET',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/messages/template-preview`,
    { template_key: templateKey, order_id: orderId }
  );
  return response.data as { subject: string; body: string };
}

/* --------------------------------- FILE REQUESTS --------------------------- */

export async function fetchFileRequests(
  candidateEntityId: string,
  orderId?: number | null
): Promise<FormFileRequest[]> {
  const response = await request(
    'GET',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/file-requests`,
    orderId ? { order_id: orderId } : {}
  );
  return response.data as FormFileRequest[];
}

export async function createFileRequest(
  candidateEntityId: string,
  payload: {
    macro_key: FileRequestMacroKey;
    publication_id: number;
    order_id: number;
    custom_message?: string;
    medium?: FormMessageMedium;
  }
): Promise<FormFileRequest> {
  const response = await request(
    'POST',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/file-requests`,
    {},
    payload
  );
  return response.data as FormFileRequest;
}

export async function resendFileRequest(
  candidateEntityId: string,
  requestId: string,
  medium: FormMessageMedium
): Promise<void> {
  await request(
    'POST',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/file-requests/${encodeURIComponent(requestId)}/resend`,
    {},
    { medium }
  );
}

export async function downloadFileRequestUpload(
  candidateEntityId: string,
  requestId: string,
  fileSha256: string
): Promise<Blob> {
  const blob = await request(
    'GET',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/file-requests/${encodeURIComponent(requestId)}/uploads/${encodeURIComponent(fileSha256)}`
  );
  return blob as Blob;
}

export async function deleteFileRequest(candidateEntityId: string, requestId: string): Promise<void> {
  await request(
    'DELETE',
    `/v1/account/candidates/${encodeURIComponent(candidateEntityId)}/file-requests/${encodeURIComponent(requestId)}`
  );
}

/* --------------------------------- SIGNATURE ------------------------------ */

export async function getSignature(): Promise<FormUserSignature> {
  const response = await request('GET', '/v1/account/signatures/me');
  return response.data as FormUserSignature;
}

export async function updateSignature(payload: {
  email_signature?: string | null;
  sms_signature?: string | null;
}): Promise<FormUserSignature> {
  const response = await request('PUT', '/v1/account/signatures/me', {}, payload);
  return response.data as FormUserSignature;
}
